import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Loader2, BookOpen, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchBlogs, fetchDiseases } from "@/lib/api";
import { searchWithFallback } from "@/lib/search";
import { sortByLatest, INITIAL_ITEMS } from "@/lib/paginate";
import { displayDiseaseNameBySlug } from "@/lib/diseaseDisplay";
import { DISEASE } from "@/constants/testIds";

export default function KnowYourDisease() {
  const [blogs, setBlogs] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [b, d] = await Promise.all([
          fetchBlogs("disease"),
          fetchDiseases(),
        ]);
        setBlogs(b || []);
        setDiseases(d || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nameForSlug = useMemo(
    () => (slug) => displayDiseaseNameBySlug(slug, diseases),
    [diseases]
  );

  const { items: filtered, fallback, empty } = useMemo(
    () =>
      searchWithFallback(
        blogs,
        q,
        (b) =>
          `${b.title} ${b.excerpt || ""} ${b.body || ""} ${nameForSlug(b.disease)}`
      ),
    [blogs, q, nameForSlug]
  );

  // "Latest 4" collapsed view — flat, newest first. Searching or expanding
  // reveals the full grouped view.
  const latestFew = useMemo(
    () => sortByLatest(filtered).slice(0, INITIAL_ITEMS),
    [filtered]
  );

  // Grouped-by-disease view. Only includes disease groups that have at least
  // one article matching the current filter (so the heading never shows
  // without articles under it).
  const sortedDiseases = useMemo(() => {
    const groups = new Map();
    diseases.forEach((d) => {
      const key = d.slug || d.id;
      if (!key) return;
      groups.set(key, { slug: key, name: nameForSlug(key) });
    });
    filtered.forEach((b) => {
      if (!b.disease) return;
      if (!groups.has(b.disease)) {
        groups.set(b.disease, {
          slug: b.disease,
          name: nameForSlug(b.disease),
        });
      }
    });
    return Array.from(groups.values())
      .filter((g) => filtered.some((b) => b.disease === g.slug))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [diseases, filtered, nameForSlug]);

  const searching = q.trim().length > 0;
  const showGrouped = expanded || searching;
  const hasMore = filtered.length > latestFew.length;

  return (
    <main data-testid={DISEASE.hub} className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="max-w-3xl">
        <div className="text-xs tracking-[0.2em] uppercase text-slate-500">Know your disease</div>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3 text-slate-900 leading-tight">
          Read, learn, take charge.
        </h1>
        <p className="text-slate-600 mt-6 text-lg leading-relaxed">
          Short, doctor-written blogs on the conditions we treat.
        </p>
      </div>

      <div className="mt-10 relative max-w-lg">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={DISEASE.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search articles — e.g. symptoms, diagnosis, diabetes…"
          className="pl-11 h-12 rounded-full bg-white"
        />
      </div>

      {loading ? (
        <div className="mt-20 flex justify-center text-slate-500">
          <Loader2 className="animate-spin mr-2" /> Loading articles…
        </div>
      ) : (
        <>
          {fallback ? (
            <p
              data-testid="disease-search-fallback"
              className="mt-6 text-sm text-slate-500"
            >
              {empty
                ? `No exact match for "${q}". Showing all related articles instead.`
                : `No exact match for "${q}". Showing related articles instead.`}
            </p>
          ) : null}

          {showGrouped ? (
            <div className="mt-10 space-y-16">
              {sortedDiseases.map((d) => {
                const items = filtered
                  .filter((b) => b.disease === d.slug)
                  .sort((a, b) => a.title.localeCompare(b.title));
                return (
                  <section key={d.slug} data-testid={DISEASE.diseaseGroup(d.slug)}>
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-6 border-b border-slate-200 pb-4">
                      <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900">
                        {d.name}
                      </h2>
                      <span className="text-xs uppercase tracking-widest text-slate-500">
                        {items.length} article{items.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {items.map((b) => (
                        <BlogCard key={b.id} b={b} disease={nameForSlug(b.disease)} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="mt-10">
              <div className="grid md:grid-cols-2 gap-6">
                {latestFew.map((b) => (
                  <BlogCard key={b.id} b={b} disease={nameForSlug(b.disease)} />
                ))}
              </div>
              {hasMore ? (
                <div className="mt-10 flex justify-center">
                  <Button
                    data-testid="disease-show-more"
                    variant="outline"
                    onClick={() => setExpanded(true)}
                    className="rounded-full h-11 px-6"
                  >
                    Show all {filtered.length} articles
                    <ChevronDown size={16} className="ml-2" />
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </main>
  );
}

function BlogCard({ b, disease }) {
  return (
    <Link
      to={`/know-your-disease/${b.id}`}
      data-testid={DISEASE.blogCard(b.id)}
      className="group"
    >
      <Card className="rounded-3xl border-slate-200 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1 h-full overflow-hidden">
        {b.cover_image ? (
          <div className="aspect-[16/9] overflow-hidden bg-slate-100">
            <img
              src={b.cover_image}
              alt={b.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        ) : null}
        <CardContent className="p-7">
          <Badge
            variant="secondary"
            className="bg-emerald-50 text-slate-700 border-emerald-100"
          >
            <BookOpen size={12} className="mr-1.5" />
            {disease}
          </Badge>
          <div className="font-serif-display text-2xl text-slate-900 mt-4 leading-snug">
            {b.title}
          </div>
          {b.excerpt ? (
            <p className="text-slate-600 mt-3 text-sm leading-relaxed line-clamp-3">
              {b.excerpt}
            </p>
          ) : null}
          <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-slate-800 group-hover:gap-2 transition-all">
            Read article <ArrowRight size={16} />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
